import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { useSearch } from '~libs/searchParams';

import { ClickableIcon, ErrorResponse, ExportDialog, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faFileCsv, faFileImport, faMagnifyingGlass } from '~components/icon';
import { TableView } from '~components/table-view-v3';
import { UploadModal } from '~components/upload-modal';

import { InvoiceStatusTransitions } from '~lookups/models';

import exportFields from '~support-at-home/forms/supplier-invoices/export';
import { columnsFactory } from '~support-at-home/forms/supplier-invoices/list';
import searchFields from '~support-at-home/forms/supplier-invoices/search';

import { useGetInvoiceStatusTransitionsQuery } from '~lookups/actions/invoiceStatuses';
import { useGetCompanySettingsQuery } from '~main/actions/companySettings';
import {
  exportAll,
  getAll,
  getEstimate,
  useSupplierInvoiceUploadMutation,
} from '~support-at-home/actions/supplierInvoices';

import {
  selectAllErrors,
  selectCurrentSupplierInvoiceEstimate,
  selectSupplierInvoices,
  selectSupplierInvoicesCount,
  selectSupplierInvoicesLoading,
  selectSupplierInvoicesTotal,
} from '~support-at-home/selectors/supplierInvoices';

import AddSupplierInvoiceButton from './AddSupplierInvoiceButton';
import EditSupplierInvoice from './EditSupplierInvoice';

interface ComponentProps {
  isActive: boolean;
  authTag?: string;
}

const SupplierInvoices: React.FC<ComponentProps> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const invoices = useAppSelector(selectSupplierInvoices);
  const invoicesLoading = useAppSelector(selectSupplierInvoicesLoading);
  const supplierInvoiceEstimate = useAppSelector(selectCurrentSupplierInvoiceEstimate);
  const supplierInvoicesTotal = useAppSelector(selectSupplierInvoicesTotal);
  const supplierInvoicesCount = useAppSelector(selectSupplierInvoicesCount);
  const errorMessages = useAppSelector(selectAllErrors);

  const uploadMutation = useSupplierInvoiceUploadMutation();

  const { data: settings = [] } = useGetCompanySettingsQuery();
  const seniorApprovalLimit = Number(settings.find((s) => s.setting.key === 'senior_approval_limit')?.value ?? 0);

  const [id, setId] = useState<string | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const isLoading = invoicesLoading === 'pending';

  const authTags = AuthTags.SUPPORTATHOME.SUPPLIERINVOICES;
  const authTagUpdate = authTags.UPDATE;

  const { data: allowedStatusTransitions = [] } = useGetInvoiceStatusTransitionsQuery();

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('invoice-search', {});
  const [params, setParams] = useState({
    sort: ['due_date.asc'],
    limit: 20,
  });

  const load = (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };

    // Keep search and load params separate as search can be cleared. Don't want to retain values when nothing is passed back.
    dispatch(getAll({ ...newParams, ...searchParams }));
    setParams(newParams);
  };

  useEffect(() => {
    if (isActive) {
      load({ limit: 20 });
    }
  }, [isActive, searchParams]);

  const download = (params: { [key: string]: any }, onProgress: (value: any) => void) =>
    exportAll({ params, onProgress });

  const openImportModal = () => setImportModalOpen(true);
  const closeImportModal = () => {
    setImportModalOpen(false);
    load();
  };

  const search = (values: { [key: string]: any }) => setSearchParams(values);
  const estimate = async (values: { [key: string]: any }) => dispatch(getEstimate(values));

  const columns = useMemo(() => columnsFactory(setId, seniorApprovalLimit), [setId, seniorApprovalLimit]);

  const invoicesWithAllowedStatuses: any[] = [];
  invoices.forEach((invoice) => {
    const allowedTransitions = allowedStatusTransitions.filter(
      (transitions: InvoiceStatusTransitions) => transitions.from_status?.tag === invoice.status?.tag,
    );
    invoicesWithAllowedStatuses.push({ ...invoice, allowedTransitions });
  });

  return (
    <div className="p-2">
      <ErrorResponse message={errorMessages} />
      <Toolbar
        id="supplier-invoices"
        rightCluster={
          <>
            <ClickableIcon
              icon={faFileCsv}
              id="sah-supplier-invoices-file-export-button"
              onClick={() => setExportDialogOpen(!exportDialogOpen)}
              authTag={authTagUpdate}
              toolTip="Export Invoices"
            />
            <ClickableIcon
              id="sah-supplier-invoices-file-upload-button"
              icon={faFileImport}
              authTag={authTagUpdate}
              toolTip="Import CSV / JSON file"
              onClick={openImportModal}
            />
            <ClickableIcon
              id="sah-supplier-invoices-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <AddSupplierInvoiceButton load={load} />
          </>
        }
      />
      <EditSupplierInvoice id={id ?? undefined} setId={setId} load={load} />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} initialValues={searchParams} onSubmit={search} />
      <TableView
        id="supplier-invoices-table"
        data={invoicesWithAllowedStatuses || []}
        columns={columns}
        isLoading={isLoading}
        readonly
        striped
        allowPagination={supplierInvoicesCount < supplierInvoicesTotal}
        onLoadMore={() => load({ limit: params.limit + 20 })}
        totalCount={supplierInvoicesTotal}
        showCount={true}
      />
      <ExportDialog
        isOpen={exportDialogOpen}
        fields={exportFields}
        openingValues={searchParams}
        estimate={supplierInvoiceEstimate}
        onClose={() => setExportDialogOpen(false)}
        onEstimate={estimate}
        onDownload={download}
      />
      <UploadModal
        isOpen={importModalOpen}
        onClose={closeImportModal}
        useUploadMutation={() => uploadMutation as [any, any]}
        header="Import Supplier Invoices"
        label="Upload Supplier Invoices CSV / JSON File"
        requirements={[
          'File must be in CSV or JSON format.',
          'Ensure all required fields are included.',
          'Maximum file size is 10MB.',
        ]}
      />
    </div>
  );
};

export default SupplierInvoices;
